import { EventBus, GameEventTypes } from '@antification/core';
import { FormicariumLayer } from '../layers/formicarium';
import { ColonyManager } from '../engine/ColonyManager';

interface MenuButton {
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
  action: () => void;
  hovered: boolean;
}

/**
 * Menu for formicarium save/load/restart operations.
 * Renders at the bottom-right of the colony view.
 * Collapsible - shows only a small toggle button when collapsed.
 */
export class FormicariumMenu {
  private events: EventBus;
  private colonyManager: ColonyManager;
  private buttons: MenuButton[] = [];
  private visible: boolean = true;
  private collapsed: boolean = true; // Start collapsed by default
  private menuX = 0;
  private menuY = 0;
  private menuWidth = 160;
  private menuHeight = 165;
  private collapsedHeight = 32; // Just the header with toggle
  private padding = 10;

  // File input for loading
  private fileInput: HTMLInputElement | null = null;

  // Toggle button hover state
  private toggleHovered: boolean = false;

  constructor(events: EventBus, colonyManager: ColonyManager) {
    this.events = events;
    this.colonyManager = colonyManager;
    this.setupFileInput();
    this.setupButtons();
  }

  private setupFileInput(): void {
    this.fileInput = document.createElement('input');
    this.fileInput.type = 'file';
    this.fileInput.accept = '.json';
    this.fileInput.style.display = 'none';
    document.body.appendChild(this.fileInput);

    this.fileInput.addEventListener('change', (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        this.loadFromFile(file);
      }
      // Reset so same file can be selected again
      if (this.fileInput) {
        this.fileInput.value = '';
      }
    });
  }

  private setupButtons(): void {
    const btnWidth = this.menuWidth - this.padding * 2;
    const btnHeight = 28;
    const btnSpacing = 8;

    this.buttons = [
      {
        label: '💾 Save Colony',
        x: 0, y: 0,
        width: btnWidth,
        height: btnHeight,
        action: () => this.saveColony(),
        hovered: false,
      },
      {
        label: '📂 Load Colony',
        x: 0, y: btnHeight + btnSpacing,
        width: btnWidth,
        height: btnHeight,
        action: () => this.loadColony(),
        hovered: false,
      },
      {
        label: '🔄 Restart Colony',
        x: 0, y: (btnHeight + btnSpacing) * 2,
        width: btnWidth,
        height: btnHeight,
        action: () => this.restartColony(),
        hovered: false,
      },
    ];
  }

  private saveColony(): void {
    const colony = this.colonyManager.getSelectedColony();
    const colonyIndex = this.colonyManager.getSelectedIndex();

    try {
      // The colony's total snapshot, the same document a district save carries
      // (ADR-006). It records which colony it is, so the file can only be loaded
      // back into that one — their tunnel layouts differ.
      const state = colony.snapshot();
      const json = JSON.stringify(state, null, 2);

      // Create download
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `colony-${colonyIndex + 1}-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      this.events.emit(GameEventTypes.NOTIFICATION_SHOW, {
        message: `Colony ${colonyIndex + 1} saved successfully`,
        type: 'success',
      });
    } catch (error) {
      console.error('Failed to save colony:', error);
      this.events.emit(GameEventTypes.NOTIFICATION_SHOW, {
        message: 'Failed to save colony',
        type: 'info',
      });
    }
  }

  private loadColony(): void {
    this.fileInput?.click();
  }

  private async loadFromFile(file: File): Promise<void> {
    try {
      const text = await file.text();
      const state = JSON.parse(text);

      const colony = this.colonyManager.getSelectedColony();
      const colonyIndex = this.colonyManager.getSelectedIndex();

      // Not `restore()`: this colony is being read from disk into a session whose
      // surface never saw its foragers, and somebody has to account for them.
      colony.restoreFromFile(state);

      this.events.emit(GameEventTypes.NOTIFICATION_SHOW, {
        message: `Colony ${colonyIndex + 1} loaded from file`,
        type: 'success',
      });
    } catch (error) {
      console.error('Failed to load colony:', error);
      this.events.emit(GameEventTypes.NOTIFICATION_SHOW, {
        message: 'Failed to load colony - invalid file',
        type: 'info',
      });
    }
  }

  private restartColony(): void {
    const colony = this.colonyManager.getSelectedColony();
    const colonyIndex = this.colonyManager.getSelectedIndex();

    colony.restart();

    this.events.emit(GameEventTypes.NOTIFICATION_SHOW, {
      message: `Colony ${colonyIndex + 1} restarted`,
      type: 'info',
    });
  }

  /**
   * Get current menu height based on collapsed state.
   */
  getCurrentHeight(): number {
    return this.collapsed ? this.collapsedHeight : this.menuHeight;
  }

  /**
   * Handle mouse move for button hover effects.
   */
  handleMouseMove(x: number, y: number, canvasWidth: number, canvasHeight: number): void {
    this.updateMenuPosition(canvasWidth, canvasHeight);

    // Check header hover (entire header is clickable)
    const headerY = this.menuY;
    const headerHeight = 32;
    this.toggleHovered = x >= this.menuX && x <= this.menuX + this.menuWidth &&
                         y >= headerY && y <= headerY + headerHeight;

    // Only check button hovers if expanded
    if (!this.collapsed) {
      for (const btn of this.buttons) {
        const btnX = this.menuX + this.padding + btn.x;
        const btnY = this.menuY + this.padding + 25 + btn.y; // +25 for header
        btn.hovered = x >= btnX && x <= btnX + btn.width &&
                      y >= btnY && y <= btnY + btn.height;
      }
    }
  }

  /**
   * Handle click - returns true if click was handled.
   */
  handleClick(x: number, y: number, canvasWidth: number, canvasHeight: number): boolean {
    if (!this.visible) return false;

    this.updateMenuPosition(canvasWidth, canvasHeight);

    // Check header/toggle click (entire header area toggles when collapsed)
    const headerY = this.menuY;
    const headerHeight = 32;
    if (x >= this.menuX && x <= this.menuX + this.menuWidth &&
        y >= headerY && y <= headerY + headerHeight) {
      this.collapsed = !this.collapsed;
      return true;
    }

    // Only check button clicks if expanded
    if (!this.collapsed) {
      for (const btn of this.buttons) {
        const btnX = this.menuX + this.padding + btn.x;
        const btnY = this.menuY + this.padding + 25 + btn.y;

        if (x >= btnX && x <= btnX + btn.width &&
            y >= btnY && y <= btnY + btn.height) {
          btn.action();
          return true;
        }
      }
    }

    return false;
  }

  private updateMenuPosition(canvasWidth: number, canvasHeight: number): void {
    const currentHeight = this.getCurrentHeight();
    this.menuX = canvasWidth - this.menuWidth - 15;
    this.menuY = canvasHeight - currentHeight - 70; // Leave room for view indicator
  }

  render(ctx: CanvasRenderingContext2D, canvasWidth: number, canvasHeight: number): void {
    if (!this.visible) return;

    this.updateMenuPosition(canvasWidth, canvasHeight);
    const currentHeight = this.getCurrentHeight();

    ctx.save();

    // Menu background
    ctx.fillStyle = 'rgba(20, 15, 10, 0.92)';
    ctx.strokeStyle = 'rgba(139, 105, 20, 0.6)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(this.menuX, this.menuY, this.menuWidth, currentHeight, 8);
    ctx.fill();
    ctx.stroke();

    // Header
    ctx.fillStyle = '#daa520';
    ctx.font = 'bold 12px "Segoe UI", system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText('Colony Menu', this.menuX + this.padding, this.menuY + 18);

    // Toggle button (collapse/expand)
    const toggleX = this.menuX + this.menuWidth - 28;
    const toggleY = this.menuY + 8;
    ctx.fillStyle = this.toggleHovered ? 'rgba(139, 105, 20, 0.4)' : 'rgba(60, 45, 30, 0.6)';
    ctx.beginPath();
    ctx.roundRect(toggleX, toggleY, 20, 16, 3);
    ctx.fill();

    // Toggle arrow
    ctx.fillStyle = this.toggleHovered ? '#ffd700' : '#c9a227';
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(this.collapsed ? '▼' : '▲', toggleX + 10, toggleY + 8);

    // Only render buttons if expanded
    if (!this.collapsed) {
      // Separator
      ctx.strokeStyle = 'rgba(139, 105, 20, 0.4)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(this.menuX + this.padding, this.menuY + 32);
      ctx.lineTo(this.menuX + this.menuWidth - this.padding, this.menuY + 32);
      ctx.stroke();

      // Buttons
      for (const btn of this.buttons) {
        const btnX = this.menuX + this.padding + btn.x;
        const btnY = this.menuY + this.padding + 25 + btn.y;

        // Button background
        ctx.fillStyle = btn.hovered
          ? 'rgba(139, 105, 20, 0.4)'
          : 'rgba(60, 45, 30, 0.6)';
        ctx.beginPath();
        ctx.roundRect(btnX, btnY, btn.width, btn.height, 4);
        ctx.fill();

        // Button border
        ctx.strokeStyle = btn.hovered
          ? 'rgba(218, 165, 32, 0.8)'
          : 'rgba(139, 105, 20, 0.4)';
        ctx.lineWidth = 1;
        ctx.stroke();

        // Button text
        ctx.fillStyle = btn.hovered ? '#ffd700' : '#c9a227';
        ctx.font = '12px "Segoe UI", system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(btn.label, btnX + btn.width / 2, btnY + btn.height / 2);
      }
    }

    ctx.restore();
  }

  /**
   * Toggle menu visibility.
   */
  toggle(): void {
    this.visible = !this.visible;
  }

  /**
   * Check if menu is visible.
   */
  isVisible(): boolean {
    return this.visible;
  }

  /**
   * Check if menu is collapsed.
   */
  isCollapsed(): boolean {
    return this.collapsed;
  }

  /**
   * Set collapsed state.
   */
  setCollapsed(collapsed: boolean): void {
    this.collapsed = collapsed;
  }

  /**
   * Clean up resources.
   */
  destroy(): void {
    if (this.fileInput && this.fileInput.parentNode) {
      this.fileInput.parentNode.removeChild(this.fileInput);
    }
  }
}
